import { forwardRef, useState, useEffect, useCallback } from 'react';
import { MdDelete } from 'react-icons/md';

import { Modal, ModalMethods, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';
import taxIcon from '../../../../assets/icons/Icon.png';

type PricingRule = {
  id?: number;
  operator: string;
  price: string;
  gst: number;
};

type Tax = {
  id?: number;
  tax_type: 'FLAT' | 'ADVANCE';
  tax_name: string;
  flat_gst: number | null;
  pricing_rules: PricingRule[];
  is_default?: boolean;
};

type EditableFlatTax = {
  key: string;
  name: string;
  gst: string;
  isDefault: boolean;
};

type EditableAdvancedRule = {
  key: string;
  operator: string;
  price: string;
  gst: string;
};

type EditableAdvancedTax = {
  key: string;
  name: string;
  rules: EditableAdvancedRule[];
  isDefault: boolean;
};

type ModifyTaxModalProps = {
  productId: number;
  taxes: Tax[];
  onSaved: () => void;
};

let keyCounter = 0;
const nextKey = () => `tax-${++keyCounter}`;

const OPERATOR_OPTIONS = ['>', '>=', '<', '<=', '='];

const taxToEditableFlat = (tax: Tax): EditableFlatTax => ({
  key: nextKey(),
  name: tax.tax_name || '',
  gst: tax.flat_gst != null ? String(tax.flat_gst) : '',
  isDefault: tax.is_default === true,
});

const taxToEditableAdvanced = (tax: Tax): EditableAdvancedTax => ({
  key: nextKey(),
  name: tax.tax_name || '',
  rules:
    tax.pricing_rules.length > 0
      ? tax.pricing_rules.map((r) => ({
          key: nextKey(),
          operator: r.operator || '>',
          price: r.price || '0',
          gst: String(r.gst),
        }))
      : [{ key: nextKey(), operator: '>', price: '0', gst: '' }],
  isDefault: tax.is_default === true,
});

const ModifyTaxModal = forwardRef<ModalMethods, ModifyTaxModalProps>(
  ({ productId, taxes, onSaved }, ref) => {
    const [flatTaxes, setFlatTaxes] = useState<EditableFlatTax[]>([]);
    const [advancedTaxes, setAdvancedTaxes] = useState<EditableAdvancedTax[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      const flats: EditableFlatTax[] = [];
      const advanced: EditableAdvancedTax[] = [];

      taxes.forEach((tax) => {
        if (tax.tax_type === 'FLAT') {
          flats.push(taxToEditableFlat(tax));
        } else {
          advanced.push(taxToEditableAdvanced(tax));
        }
      });

      if (flats.length === 0) {
        flats.push({ key: nextKey(), name: '', gst: '', isDefault: false });
      }

      if (advanced.length === 0) {
        advanced.push({
          key: nextKey(),
          name: '',
          rules: [{ key: nextKey(), operator: '>', price: '0', gst: '' }],
          isDefault: false,
        });
      }

      setFlatTaxes(flats);
      setAdvancedTaxes(advanced);
    }, [taxes]);

    const handleAddFlatTax = useCallback(() => {
      setFlatTaxes((prev) => [...prev, { key: nextKey(), name: '', gst: '', isDefault: false }]);
    }, []);

    const handleRemoveFlatTax = useCallback((key: string) => {
      setFlatTaxes((prev) => prev.filter((t) => t.key !== key));
    }, []);

    const handleFlatChange = useCallback(
      (key: string, field: keyof EditableFlatTax, value: string | boolean) => {
        setFlatTaxes((prev) => prev.map((t) => (t.key === key ? { ...t, [field]: value } : t)));
      },
      []
    );

    const handleAddAdvancedTax = useCallback(() => {
      setAdvancedTaxes((prev) => [
        ...prev,
        {
          key: nextKey(),
          name: '',
          rules: [{ key: nextKey(), operator: '>', price: '0', gst: '' }],
          isDefault: false,
        },
      ]);
    }, []);

    const handleRemoveAdvancedTax = useCallback((key: string) => {
      setAdvancedTaxes((prev) => prev.filter((t) => t.key !== key));
    }, []);

    const handleAdvancedChange = useCallback(
      (key: string, field: 'name' | 'isDefault', value: string | boolean) => {
        setAdvancedTaxes((prev) => prev.map((t) => (t.key === key ? { ...t, [field]: value } : t)));
      },
      []
    );

    const handleAddRule = useCallback((taxKey: string) => {
      setAdvancedTaxes((prev) =>
        prev.map((t) =>
          t.key === taxKey
            ? { ...t, rules: [...t.rules, { key: nextKey(), operator: '>', price: '', gst: '' }] }
            : t
        )
      );
    }, []);

    const handleRemoveRule = useCallback((taxKey: string, ruleKey: string) => {
      setAdvancedTaxes((prev) =>
        prev.map((t) =>
          t.key === taxKey ? { ...t, rules: t.rules.filter((r) => r.key !== ruleKey) } : t
        )
      );
    }, []);

    const handleRuleChange = useCallback(
      (taxKey: string, ruleKey: string, field: keyof EditableAdvancedRule, value: string) => {
        setAdvancedTaxes((prev) =>
          prev.map((t) =>
            t.key === taxKey
              ? {
                  ...t,
                  rules: t.rules.map((r) => (r.key === ruleKey ? { ...r, [field]: value } : r)),
                }
              : t
          )
        );
      },
      []
    );

    const handleSave = useCallback(async () => {
      const taxPayload: Record<string, unknown>[] = [];

      for (const flat of flatTaxes) {
        if (!flat.name.trim()) {
          toasts('error', 'Tax name is required for all flat taxes', 'flat-tax-name-required');

          return;
        }

        if (!flat.gst.trim() || isNaN(Number(flat.gst))) {
          toasts('error', 'Valid tax percentage is required', 'flat-tax-gst-required');

          return;
        }

        taxPayload.push({
          tax_type: 'FLAT',
          flat_gst: { name: flat.name.trim(), gst: Number(flat.gst) },
          is_default: flat.isDefault,
        });
      }

      for (const adv of advancedTaxes) {
        if (!adv.name.trim()) {
          toasts('error', 'Tax name is required for all advanced taxes', 'adv-tax-name-required');

          return;
        }

        if (adv.rules.length === 0) {
          toasts(
            'error',
            'At least one rule is required for advanced taxes',
            'adv-tax-rules-required'
          );

          return;
        }

        for (const rule of adv.rules) {
          if (!rule.price.trim() || isNaN(Number(rule.price))) {
            toasts('error', 'Valid price is required in all rules', 'adv-tax-rule-price-required');

            return;
          }

          if (!rule.gst.trim() || isNaN(Number(rule.gst))) {
            toasts('error', 'Valid tax % is required in all rules', 'adv-tax-rule-gst-required');

            return;
          }
        }

        taxPayload.push({
          tax_type: 'ADVANCE',
          advance_tax_name: adv.name.trim(),
          pricing_rules: adv.rules.map((r) => ({
            operator: r.operator,
            price: r.price.trim(),
            gst: Number(r.gst),
          })),
          is_default: adv.isDefault,
        });
      }

      setIsSaving(true);

      try {
        const response = await api.putRequest(`readymade/${productId}`, { taxes: taxPayload });

        if (response.status) {
          toasts('success', 'Taxes updated successfully', 'tax-update-success');
          onSaved();

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }
        } else {
          toasts('error', response.message || 'Failed to update taxes', 'tax-update-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'tax-update-error');
        }
      } finally {
        setIsSaving(false);
      }
    }, [flatTaxes, advancedTaxes, productId, onSaved, ref]);

    const handleBack = useCallback(() => {
      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    }, [ref]);

    return (
      <Modal ref={ref} size="xl" showFooter={false} showCloseIcon={false}>
        <div className="flex flex-col min-h-[500px] p-4">
          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 font-medium"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 12L6 8l4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back
            </button>

            <div className="flex items-center gap-2">
              <img src={taxIcon} alt="Tax" className="w-5 h-5 object-contain" />
              <p className="font-inter font-bold text-[1.1rem]">Enter tax</p>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>

          <div className="text-center py-4">
            <p className="font-inter text-sm text-gray-500">
              Set the and tax for this item. All values will be applied automatically during
              billing.
            </p>
            <p className="font-inter text-sm text-gray-500">
              Check the box{' '}
              <span className="inline-flex items-center justify-center w-4 h-4 bg-blue-600 rounded align-middle">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 6l3 3 5-5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>{' '}
              to make it a default tax
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-1">
            <p className="font-inter font-bold text-[15px] text-gray-900 mb-4">
              Tax rules (item level)
            </p>

            <div className="space-y-5">
              {flatTaxes.map((flat) => (
                <div key={flat.key} className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-block px-4 py-1.5 bg-gray-100 rounded-md text-sm font-semibold text-gray-800">
                      Flat
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFlatTax(flat.key)}
                      className="p-1.5 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>

                  <div className="flex gap-4 mb-4">
                    <div className="flex-1">
                      <p className="font-inter text-sm font-semibold text-gray-700 mb-1.5">
                        Tax Name
                      </p>
                      <input
                        type="text"
                        value={flat.name}
                        onChange={(e) => handleFlatChange(flat.key, 'name', e.target.value)}
                        placeholder="e.g. GST"
                        className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="font-inter text-sm font-semibold text-gray-700 mb-1.5">
                        Tax Percentage (%)
                      </p>
                      <input
                        type="text"
                        value={flat.gst}
                        onChange={(e) => handleFlatChange(flat.key, 'gst', e.target.value)}
                        placeholder="e.g. 12"
                        className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleFlatChange(flat.key, 'isDefault', !flat.isDefault)}
                      className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                        flat.isDefault ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                      }`}
                    >
                      {flat.isDefault && (
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M2 6l3 3 5-5"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                    <span className="text-sm font-medium text-gray-700">Default</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 ml-6">
                    Default tax will be automatically applied to an order
                  </p>
                </div>
              ))}

              {advancedTaxes.map((adv) => (
                <div key={adv.key} className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-block px-4 py-1.5 bg-gray-100 rounded-md text-sm font-semibold text-gray-800">
                      Advanced
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveAdvancedTax(adv.key)}
                      className="p-1.5 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>

                  <div className="flex items-center gap-3 mb-4">
                    <p className="font-inter text-sm font-semibold text-gray-700 whitespace-nowrap">
                      Tax Name
                    </p>
                    <input
                      type="text"
                      value={adv.name}
                      onChange={(e) => handleAdvancedChange(adv.key, 'name', e.target.value)}
                      placeholder="e.g. GST"
                      className="w-48 h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="space-y-3 mb-4">
                    {adv.rules.map((rule) => (
                      <div key={rule.key} className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-500 font-medium">Price</span>
                        <select
                          value={rule.operator}
                          onChange={(e) =>
                            handleRuleChange(adv.key, rule.key, 'operator', e.target.value)
                          }
                          className="h-9 px-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {OPERATOR_OPTIONS.map((op) => (
                            <option key={op} value={op}>
                              {op}
                            </option>
                          ))}
                        </select>
                        <span className="text-sm text-gray-500">₹</span>
                        <input
                          type="text"
                          value={rule.price}
                          onChange={(e) =>
                            handleRuleChange(adv.key, rule.key, 'price', e.target.value)
                          }
                          placeholder="0"
                          className="w-24 h-9 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-500">→</span>
                        <span className="text-sm text-gray-500 font-medium">Tax</span>
                        <input
                          type="text"
                          value={rule.gst}
                          onChange={(e) =>
                            handleRuleChange(adv.key, rule.key, 'gst', e.target.value)
                          }
                          placeholder="0"
                          className="w-16 h-9 px-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-500">%</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveRule(adv.key, rule.key)}
                          className="p-1 text-red-400 hover:text-red-600 transition-colors"
                        >
                          <MdDelete size={16} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleAddRule(adv.key)}
                    className="text-blue-600 text-sm font-medium hover:text-blue-700 mb-4"
                  >
                    + Add Rule
                  </button>

                  <div className="flex items-center gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => handleAdvancedChange(adv.key, 'isDefault', !adv.isDefault)}
                      className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                        adv.isDefault ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                      }`}
                    >
                      {adv.isDefault && (
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M2 6l3 3 5-5"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                    <span className="text-sm font-medium text-gray-700">Default</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 ml-6">
                    Default tax will be automatically applied to an order
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200 mt-4">
            <button
              type="button"
              onClick={handleAddAdvancedTax}
              className="px-4 py-2 border border-blue-500 text-blue-600 rounded-full text-sm font-medium hover:bg-blue-50 transition-colors"
            >
              Advance Tax +
            </button>
            <button
              type="button"
              onClick={handleAddFlatTax}
              className="px-4 py-2 border border-blue-500 text-blue-600 rounded-full text-sm font-medium hover:bg-blue-50 transition-colors"
            >
              Flat Tax +
            </button>
          </div>
        </div>
      </Modal>
    );
  }
);

ModifyTaxModal.displayName = 'ModifyTaxModal';

export default ModifyTaxModal;
